import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { Plus, Trash2, TrendingUp, Award, Calendar } from 'lucide-react';
import { usePoints } from '../App';
import { confirmDialog } from '../components/ui/confirm-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HabitsPage = ({ TopHeader, BottomNav, useAuth }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchHabits();
  }, []);

  const fetchHabits = async () => {
    try {
      const response = await axios.get(`${API}/habits`);
      setHabits(response.data);
    } catch (error) {
      console.error('Failed to fetch habits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (habitId) => {
    if (await confirmDialog({ title: 'Delete habit?', message: 'This action cannot be undone.', confirmText: 'Delete', danger: true })) {
      try {
        await axios.delete(`${API}/habits/${habitId}`);
        fetchHabits();
      } catch (error) {
        console.error('Failed to delete habit:', error);
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page-container">
      <TopHeader title="My Habits" showBack={false} />
      <div className="content-wrapper">
        <div className="content-section">
          <div style={{ marginBottom: 24, padding: 20, background: 'linear-gradient(135deg, #F0FDFA, #FEF3C7)', borderRadius: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--text-dark)' }}>Track Your Daily Habits</h3>
            <p style={{ fontSize: 14, color: 'var(--text-medium)' }}>Build consistency with personal habit tracking. Solo activities help you stay accountable!</p>
          </div>

          <button
            className="btn-create"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-habit-btn"
          >
            <Plus size={20} style={{ display: 'inline', marginRight: 8 }} />
            Create New Habit
          </button>

          {habits.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✨</div>
              <h3 className="empty-title">No Habits Yet</h3>
              <p className="empty-description">
                Start building healthy habits today! Track steps, meditation, water intake, or create custom habits.
              </p>
            </div>
          ) : (
            <div className="habits-grid" data-testid="habits-list">
              {habits.map(habit => (
                <HabitCard
                  key={habit.id}
                  habit={habit}
                  onDelete={handleDelete}
                  onClick={() => navigate(`/habits/${habit.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNav active="habits" />

      {showCreateModal && (
        <CreateHabitModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchHabits();
          }}
        />
      )}
    </div>
  );
};

const HabitCard = ({ habit, onDelete, onClick }) => {
  const getHabitIcon = (type) => {
    const icons = {
      steps: '🚶',
      meditation: '🧘',
      water: '💧',
      exercise: '💪',
      custom: '⭐'
    };
    
    // For custom habits, try to infer icon from title
    if (type === 'custom') {
      const title = habit.title.toLowerCase();
      if (title.includes('gratitude') || title.includes('journal')) return '📝';
      if (title.includes('recycl') || title.includes('waste')) return '♻️';
      if (title.includes('read')) return '📚';
    }
    
    return icons[type] || '⭐';
  };

  return (
    <div className="habit-card-compact" onClick={onClick} data-testid="habit-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
        <div style={{ fontSize: 28 }}>{getHabitIcon(habit.habit_type)}</div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(habit.id); }}
          style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', padding: 4 }}
          data-testid="delete-habit-btn"
        >
          <Trash2 size={16} />
        </button>
      </div>
      
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--text-dark)' }}>{habit.title}</h3>
      <p className="habit-type">{habit.description}</p>
      
      <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-light)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary-teal)' }}>
            {habit.streak_count}🔥
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-medium)' }}>Streak</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>
            {habit.total_completed}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-medium)' }}>Total</div>
        </div>
      </div>
    </div>
  );
};

const CreateHabitModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    habit_type: 'steps',
    target_value: 10000,
    frequency: 'daily'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/habits`, formData);
      onCreated();
    } catch (error) {
      console.error('Failed to create habit:', error);
      alert('Failed to create habit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="create-habit-modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <h2 className="modal-title">Create New Habit</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Habit Type</label>
            <select
              className="form-input"
              value={formData.habit_type}
              onChange={(e) => setFormData({ ...formData, habit_type: e.target.value })}
              data-testid="habit-type-select"
            >
              <option value="steps">🚶 Steps</option>
              <option value="meditation">🧘 Meditation</option>
              <option value="water">💧 Water Intake</option>
              <option value="exercise">💪 Exercise</option>
              <option value="custom">⭐ Custom</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Habit Title</label>
            <input
              type="text"
              className="form-input"
              placeholder="Daily 10k Steps"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="habit-title-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              placeholder="Walk 10,000 steps every day"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              required
              data-testid="habit-description-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Daily Target</label>
            <input
              type="number"
              className="form-input"
              placeholder="10000"
              value={formData.target_value}
              onChange={(e) => setFormData({ ...formData, target_value: parseInt(e.target.value) })}
              min="1"
              required
              data-testid="habit-target-input"
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
            data-testid="submit-habit-btn"
          >
            {submitting ? 'Creating...' : 'Create Habit'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            style={{ marginTop: 12 }}
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};

const HeatmapCalendar = ({ logs }) => {
  // Generate last 12 weeks of data (84 days)
  const generateCalendarData = () => {
    const weeks = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 83); // 12 weeks back
    
    // Create a map of dates that have logs
    const logMap = new Map();
    logs.forEach(log => {
      const date = new Date(log.date).toDateString();
      logMap.set(date, log);
    });
    
    // Generate 12 weeks
    for (let week = 0; week < 12; week++) {
      const weekData = [];
      for (let day = 0; day < 7; day++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + (week * 7) + day);
        
        const dateStr = currentDate.toDateString();
        const log = logMap.get(dateStr);
        
        weekData.push({
          date: currentDate,
          hasLog: !!log,
          completed: log?.completed || false,
          value: log?.value || 0
        });
      }
      weeks.push(weekData);
    }
    
    return weeks;
  };
  
  const weeks = generateCalendarData();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getCellColor = (dayData) => {
    if (!dayData.hasLog) return '#EBEDF0';
    if (dayData.completed) return '#10B981'; // Green
    return '#F59E0B'; // Orange for partial
  };
  
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Activity Calendar</h3>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 3, minWidth: 'fit-content' }}>
          {/* Day labels */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingRight: 8 }}>
            <div style={{ height: 12 }} />
            {weekDays.map((day, i) => (
              <div 
                key={i} 
                style={{ 
                  fontSize: 9, 
                  color: 'var(--text-medium)', 
                  height: 12, 
                  lineHeight: '12px',
                  display: i % 2 === 1 ? 'block' : 'none' // Show only alternate days
                }}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Month label (show on first day of month) */}
              <div style={{ height: 12, fontSize: 9, color: 'var(--text-medium)', fontWeight: 600 }}>
                {week[0].date.getDate() <= 7 ? week[0].date.toLocaleDateString('en', { month: 'short' }) : ''}
              </div>
              
              {week.map((dayData, dayIndex) => (
                <div
                  key={dayIndex}
                  title={`${dayData.date.toLocaleDateString()} ${dayData.hasLog ? `- ${dayData.value}` : '- No activity'}`}
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: getCellColor(dayData),
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.3)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, fontSize: 11, color: 'var(--text-medium)' }}>
        <span>Less</span>
        <div style={{ display: 'flex', gap: 3 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#EBEDF0', borderRadius: 2 }} />
          <div style={{ width: 12, height: 12, backgroundColor: '#F59E0B', borderRadius: 2 }} />
          <div style={{ width: 12, height: 12, backgroundColor: '#10B981', borderRadius: 2 }} />
        </div>
        <span>More</span>
      </div>
    </div>
  );
};

export const HabitDetailPage = ({ TopHeader, BottomNav }) => {
  const navigate = useNavigate();
  const { refreshPoints } = usePoints();
  const habitId = window.location.pathname.split('/').pop();
  const [habit, setHabit] = useState(null);
  const [logs, setLogs] = useState([]);
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [habitId]);

  const fetchData = async () => {
    try {
      const [habitRes, logsRes] = await Promise.all([
        axios.get(`${API}/habits/${habitId}`),
        axios.get(`${API}/habits/${habitId}/logs?limit=90`)
      ]);
      setHabit(habitRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch habit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLog = async (e) => {
    e.preventDefault();
    if (value <= 0) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/habits/${habitId}/log`, {
        value: parseInt(value),
        date: new Date().toISOString()
      });
      setValue(0);
      fetchData();
      refreshPoints(); // Refresh points in navbar
    } catch (error) {
      console.error('Failed to log habit:', error);
      alert('Failed to log habit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!habit) {
    return <div className="loading">Habit not found</div>;
  }

  const getHabitIcon = (habit) => {
    const icons = {
      steps: '🚶',
      meditation: '🧘',
      water: '💧',
      exercise: '💪',
      custom: '⭐'
    };
    
    // For custom habits, try to infer icon from title
    if (habit.habit_type === 'custom') {
      const title = habit.title.toLowerCase();
      if (title.includes('gratitude') || title.includes('journal')) return '📝';
      if (title.includes('recycl') || title.includes('waste')) return '♻️';
      if (title.includes('read')) return '📚';
    }
    
    return icons[habit.habit_type] || '⭐';
  };

  return (
    <div className="page-container">
      <TopHeader title="Habit Details" showBack={true} />
      <div className="content-wrapper">
        <div className="content-section">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>
              {getHabitIcon(habit)}
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{habit.title}</h2>
            <p style={{ fontSize: 15, color: 'var(--text-medium)' }}>{habit.description}</p>
          </div>

          <div className="stats-grid" style={{ marginBottom: 32 }}>
            <div className="stat-card">
              <span className="stat-card-value">{habit.streak_count}🔥</span>
              <span className="stat-card-label">Current Streak</span>
            </div>
            <div className="stat-card">
              <span className="stat-card-value">{habit.best_streak}</span>
              <span className="stat-card-label">Best Streak</span>
            </div>
            <div className="stat-card">
              <span className="stat-card-value">{habit.total_completed}</span>
              <span className="stat-card-label">Completed</span>
            </div>
            <div className="stat-card">
              <span className="stat-card-value">{habit.target_value}</span>
              <span className="stat-card-label">Daily Goal</span>
            </div>
          </div>

          <HeatmapCalendar logs={logs} />

          <h3 className="section-title" style={{ marginBottom: 16 }}>Log Today's Progress</h3>
          <form onSubmit={handleLog}>
            <div className="form-group">
              <input
                type="number"
                className="form-input"
                style={{ fontSize: 24, textAlign: 'center', padding: 20 }}
                placeholder="Enter value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                min="0"
                required
                data-testid="log-habit-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || value <= 0}
              data-testid="submit-habit-log-btn"
            >
              {submitting ? 'Logging...' : 'Log Progress'}
            </button>
          </form>

          <h3 className="section-title" style={{ marginTop: 32, marginBottom: 16 }}>Recent Activity</h3>
          {logs.length === 0 ? (
            <div className="empty-state">
              <p className="empty-description">No activity logged yet</p>
            </div>
          ) : (
            <div>
              {logs.slice(0, 10).map((log, index) => (
                <div
                  key={log.id}
                  className="card"
                  style={{ padding: 16, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      {log.value} {habit.habit_type}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>
                      {new Date(log.date).toLocaleDateString()}
                    </div>
                  </div>
                  {log.completed && <span style={{ fontSize: 24 }}>✅</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNav active="habits" />
    </div>
  );
};